import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuth';
import type { CreateJobResponse } from '../import-review/types';

type CreateUploadUrlResponse = {
  uploadUrl: string;
  sourceR2Key: string;
  expiresIn: number;
  uploadHeaders?: Record<string, string>;
};

const PDF_MIME_TYPE = 'application/pdf';
const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * Map a chosen `File` to the MIME type we'll send to the upload-URL
 * gate. Browsers reliably set `file.type` on PDFs but inconsistently on
 * DOCX (some return empty, some return the OOXML mime). Falls back to
 * the extension when the browser doesn't tell us. Kept aligned with
 * `PROCESS_DOC_UPLOAD_CONTENT_TYPES` in
 * `src/domains/process-doc-jobs/controllers/ProcessDocJobController.ts`.
 */
function resolveSopMimeType(file: File): string {
  const normalized = (file.type || '').trim().toLowerCase();
  if (normalized === PDF_MIME_TYPE) return PDF_MIME_TYPE;
  if (normalized === DOCX_MIME_TYPE) return DOCX_MIME_TYPE;
  const name = file.name.trim().toLowerCase();
  if (!normalized && name.endsWith('.pdf')) return PDF_MIME_TYPE;
  if (!normalized && name.endsWith('.docx')) return DOCX_MIME_TYPE;
  throw new Error('Only PDF or Word (.docx) files are supported.');
}

/**
 * Sleep for a bounded number of ms. Returns a promise so callers can
 * await it inline.
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function sha256FileHash(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer(),
  );
  const hex = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  return `sha256:${hex}`;
}

interface CreateSopFileJobInput {
  file: File;
  contentHash?: string | null;
  supersedesSourceId?: string | null;
}

/**
 * Parse the server-provided retry window. Prefers JSON body's
 * `retryAfterSeconds`, falls back to the `Retry-After` header (seconds
 * or HTTP-date), defaults to 5s if neither is present. Cap at 60s so
 * a misbehaving server can't stall the loop indefinitely.
 */
async function parseRetryAfter(resp: Response): Promise<number> {
  let seconds: number | null = null;
  try {
    const cloned = resp.clone();
    const body = (await cloned.json()) as { retryAfterSeconds?: number };
    if (
      typeof body.retryAfterSeconds === 'number' &&
      body.retryAfterSeconds > 0
    ) {
      seconds = body.retryAfterSeconds;
    }
  } catch {
    /* body wasn't JSON — fall through to header */
  }
  if (seconds === null) {
    const header = resp.headers.get('retry-after');
    if (header) {
      const asNumber = Number(header);
      if (!Number.isNaN(asNumber) && asNumber > 0) {
        seconds = asNumber;
      } else {
        const asDate = Date.parse(header);
        if (!Number.isNaN(asDate)) {
          seconds = Math.max(1, Math.ceil((asDate - Date.now()) / 1000));
        }
      }
    }
  }
  return Math.min(60, Math.max(1, seconds ?? 5));
}

/**
 * Hook for dispatching a single SOP / policy file through the
 * upload-URL → PUT → create-and-dispatch chain.
 *
 * @param folderId       Folder the job will publish into.
 * @param uploadBatchId  Optional grouping key set by the caller when
 *                       multiple files are dispatched in one
 *                       "Extract N files" click. The Reviews tab uses
 *                       this to collapse N per-file rows into a single
 *                       batch entry. Pass the same value for every
 *                       file in a batch; leave undefined for singletons.
 */
export function useCreateSopFileJob(folderId: string, uploadBatchId?: string) {
  const fetcher = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: File | CreateSopFileJobInput) => {
      const file = input instanceof File ? input : input.file;
      const mimeType = resolveSopMimeType(file);
      const contentHash =
        input instanceof File
          ? await sha256FileHash(file)
          : (input.contentHash ?? (await sha256FileHash(file)));
      const supersedesSourceId =
        input instanceof File ? null : input.supersedesSourceId?.trim() || null;

      // Retry the upload-URL request once on 429 honoring the server's
      // Retry-After window. The FileUploadStage batch loop already
      // paces client-side to stay under the per-minute budget; this
      // retry is belt-and-braces for the rare race where two tabs (or
      // two batches) compete for the same user's budget.
      const requestUploadUrl = async () =>
        fetcher('/api/v1/process-doc-jobs/upload-url', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            contentType: mimeType,
            contentLength: file.size,
            targetFolderId: folderId,
          }),
        });

      let uploadUrlResponse = await requestUploadUrl();
      if (uploadUrlResponse.status === 429) {
        const wait = await parseRetryAfter(uploadUrlResponse);
        await delay(wait * 1000);
        uploadUrlResponse = await requestUploadUrl();
      }
      if (!uploadUrlResponse.ok) {
        if (uploadUrlResponse.status === 429) {
          throw new Error(
            'Upload paused — too many requests. Wait a minute and try again.',
          );
        }
        throw new Error('Failed to initialize document upload');
      }

      const uploadPayload =
        (await uploadUrlResponse.json()) as CreateUploadUrlResponse;
      if (!uploadPayload.uploadUrl || !uploadPayload.sourceR2Key) {
        throw new Error('Invalid document upload response');
      }

      const putResponse = await fetch(uploadPayload.uploadUrl, {
        method: 'PUT',
        headers: uploadPayload.uploadHeaders ?? {
          'Content-Type': mimeType,
        },
        body: file,
      });
      if (!putResponse.ok) {
        throw new Error('Failed to upload document bytes');
      }

      const createAndDispatchResponse = await fetcher(
        '/api/v1/process-doc-jobs/create-and-dispatch',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            sourceType: 'r2',
            sourceR2Key: uploadPayload.sourceR2Key,
            sourceFileName: file.name,
            targetFolderId: folderId,
            metadata: {
              sourceContentHash: contentHash,
              ...(supersedesSourceId
                ? { sourceLineage: { supersedesSourceId } }
                : {}),
            },
            // Only thread through when set — the server rejects empty
            // strings (Zod regex is strict). Single-file uploads from
            // callers that don't generate a batch id leave it absent.
            ...(uploadBatchId ? { uploadBatchId } : {}),
          }),
        },
      );
      if (!createAndDispatchResponse.ok)
        throw new Error('Failed to create and dispatch import job');
      const payload =
        (await createAndDispatchResponse.json()) as CreateJobResponse;

      return payload.job;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['folder-jobs'] }),
        queryClient.invalidateQueries({
          queryKey: ['process-doc-review', 'jobs'],
        }),
      ]);
    },
  });
}
